/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, ChangeEvent, FocusEvent } from 'react';
import { Controller, Control } from 'react-hook-form';
import { InputAdornment } from '@mui/material';

import TestIds from '../../../testIds';
import InputFormControl from '../../form-controls/InputFormControl/InputFormControl';
import { EUnitPriceFields, TUnitPriceFormData } from './UnitPriceForm';
import {
  formatPriceInput,
  isValidCurrencyAmount,
  isValidPriceInput,
} from '../../../util/formatting';

export interface IFormControlUnitPriceProps {
  control: Control<TUnitPriceFormData>;
  validateField: (errorMessage: string) => (value?: string) => string | true;
  clearError: () => void;
  onChange?: (value: string) => void;
  onFocus?: () => void;
}

export const FormControlUnitPrice: FC<IFormControlUnitPriceProps> = ({
  control,
  validateField,
  clearError,
  onChange,
  onFocus,
}) => {
  const validateUnitPrice = (value?: string): string | true => {
    const errorMessage = 'Please enter a valid unit price';
    if (!isValidCurrencyAmount(value?.trim() || '')) {
      return errorMessage;
    }

    if (parseFloat(value || '0') <= 0) {
      return 'Please enter a unit price greater than $0';
    }

    return validateField(errorMessage)(value);
  };

  return (
    <Controller
      name={EUnitPriceFields.UnitPrice}
      control={control}
      rules={{ validate: validateUnitPrice }}
      render={({ field, fieldState: { error } }) => (
        <InputFormControl
          id={EUnitPriceFields.UnitPrice}
          data-testid={TestIds.components.forms.unitPriceForm.unitPriceField}
          variant='farmlands2.0_default'
          labelColor='secondary'
          type='text'
          inputMode='decimal'
          label='Unit price (GST excl.)'
          placeholder={field.value}
          error={!!error}
          required
          value={field.value}
          errorMessage={error?.message}
          startAdornment={<InputAdornment position='start'>$</InputAdornment>}
          inputProps={{
            autoComplete: 'off',
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;

              if (isValidPriceInput(value)) {
                field.onChange(value);
                onChange?.(value);
                error && clearError();
              }
            },
            onBlur: (event: FocusEvent<HTMLInputElement>) => {
              const formattedValue = formatPriceInput(event.target.value);

              if (formattedValue) {
                field.onChange(formattedValue);
              }

              field.onBlur();
            },
            onFocus: onFocus,
          }}
        />
      )}
    />
  );
};
